import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  Star, Send, CheckCircle, MessageSquare,
  Lightbulb, Bug, AlertCircle, FileText,
  Calendar, Trash2, Clock, MessageCircle,
} from 'lucide-react';
import NotificationModal from '../components/NotificationModal';

// ─── Config ──────────────────────────────────────────────────────────────────

const FEEDBACK_TYPES = [
  { value: 'general_feedback', label: 'General Feedback', icon: MessageSquare, color: 'text-blue-600',   bg: 'bg-blue-50'   },
  { value: 'bug_report',       label: 'Bug Report',       icon: Bug,            color: 'text-red-600',    bg: 'bg-red-50'    },
  { value: 'feature_request',  label: 'Feature Request',  icon: Lightbulb,      color: 'text-amber-600',  bg: 'bg-amber-50'  },
  { value: 'suggestion',       label: 'Suggestion',       icon: FileText,       color: 'text-green-600',  bg: 'bg-green-50'  },
  { value: 'complaint',        label: 'Complaint',        icon: AlertCircle,    color: 'text-orange-600', bg: 'bg-orange-50' },
];

const CATEGORIES = [
  { value: 'other',         label: 'General' },
  { value: 'ui_ux',         label: 'UI / Experience' },
  { value: 'performance',   label: 'Performance' },
  { value: 'functionality', label: 'Functionality' },
  { value: 'integration',   label: 'Integrations' },
  { value: 'documentation', label: 'Documentation' },
];

function formatDate(str) {
  return new Date(str).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ─── Star rating ─────────────────────────────────────────────────────────────

const StarRating = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(value === n ? 0 : n)}
        className="focus:outline-none"
        title={n === value ? 'Click to clear' : `${n} star${n > 1 ? 's' : ''}`}
      >
        <Star
          className={`w-7 h-7 transition-colors ${
            n <= value ? 'text-amber-400 fill-amber-400' : 'text-gray-300 hover:text-amber-300'
          }`}
        />
      </button>
    ))}
    {value > 0 && (
      <span className="text-sm text-gray-500 self-center ml-1">{value}/5</span>
    )}
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────────────

const UserFeedbackForm = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab]     = useState('submit');
  const [loading, setLoading]         = useState(false);
  const [myFeedback, setMyFeedback]   = useState([]);
  const [submitted, setSubmitted]     = useState(false);
  const [notification, setNotification] = useState(null);

  const [form, setForm] = useState({
    type:        'general_feedback',
    category:    'other',
    title:       '',
    description: '',
    rating:      0,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (activeTab === 'my-feedback') fetchMyFeedback();
  }, [activeTab]);

  const fetchMyFeedback = async () => {
    try {
      setLoading(true);
      const res = await api.get('/feedback/my-feedback');
      setMyFeedback(res.data.data);
    } catch {
      // silently fail — empty state will show
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim())       e.title       = 'A short title is required.';
    else if (form.title.length > 100) e.title   = 'Title must be under 100 characters.';
    if (!form.description.trim()) e.description = 'Please describe your feedback.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      await api.post('/feedback', {
        ...form,
        rating:   form.rating || undefined,   // omit if 0 (optional field)
        priority: 'medium',
        pageUrl:  window.location.href,
        browserInfo: {
          userAgent: navigator.userAgent,
          platform:  navigator.platform,
          language:  navigator.language,
        },
      });

      setSubmitted(true);
      setForm({ type: 'general_feedback', category: 'other', title: '', description: '', rating: 0 });
      setErrors({});
      setTimeout(() => setSubmitted(false), 4000);
    } catch {
      setNotification({ variant: 'error', title: 'Submission failed', message: 'Could not submit your feedback. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const deleteFeedback = async (id) => {
    try {
      await api.delete(`/feedback/${id}`);
      setMyFeedback(prev => prev.filter(f => f._id !== id));
      setNotification({ variant: 'success', title: 'Deleted', message: 'Feedback removed.' });
    } catch (err) {
      setNotification({ variant: 'error', title: 'Delete failed', message: err.response?.data?.message || 'Please try again.' });
    }
  };

  const TABS = [
    { id: 'submit',      label: 'Submit',      icon: Send },
    { id: 'my-feedback', label: 'My Feedback', icon: MessageCircle },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-b from-white to-blue-50 border-b border-gray-100 px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Feedback</h1>
          </div>
          <p className="text-sm text-gray-500 ml-10">
            Share a thought, report a bug, or suggest a feature — every message helps.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-100 px-6">
        <div className="max-w-3xl mx-auto flex gap-1">
          {TABS.map(tab => {
            const Icon   = tab.icon;
            const active = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors duration-150 ${
                  active
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-8">

        {/* ── Submit tab ── */}
        {activeTab === 'submit' && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            {submitted && (
              <div className="mb-6 flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-emerald-800">Feedback submitted — thank you!</p>
                  <p className="text-xs text-emerald-700 mt-0.5">We'll review it and follow up if needed.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Type</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {FEEDBACK_TYPES.map(t => {
                    const Icon     = t.icon;
                    const selected = form.type === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setForm(p => ({ ...p, type: t.value }))}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-medium transition-all ${
                          selected
                            ? `border-blue-400 ${t.bg} ${t.color} ring-2 ring-blue-200`
                            : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${selected ? t.color : 'text-gray-400'}`} />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category — optional */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <select
                  id="category"
                  value={form.category}
                  onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  id="title"
                  type="text"
                  value={form.title}
                  onChange={e => {
                    setForm(p => ({ ...p, title: e.target.value }));
                    if (errors.title) setErrors(p => ({ ...p, title: '' }));
                  }}
                  maxLength={100}
                  placeholder="One-line summary"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.title ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
                <div className="flex justify-between mt-1">
                  {errors.title
                    ? <p className="text-xs text-red-600">{errors.title}</p>
                    : <span />}
                  <p className="text-xs text-gray-400">{form.title.length}/100</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  id="description"
                  rows={5}
                  value={form.description}
                  onChange={e => {
                    setForm(p => ({ ...p, description: e.target.value }));
                    if (errors.description) setErrors(p => ({ ...p, description: '' }));
                  }}
                  maxLength={2000}
                  placeholder="Tell us more — steps to reproduce, what you expected, any context that helps…"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y ${
                    errors.description ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
                <div className="flex justify-between mt-1">
                  {errors.description
                    ? <p className="text-xs text-red-600">{errors.description}</p>
                    : <span />}
                  <p className="text-xs text-gray-400">{form.description.length}/2000</p>
                </div>
              </div>

              {/* Rating — optional */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overall rating <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <StarRating value={form.rating} onChange={v => setForm(p => ({ ...p, rating: v }))} />
                {form.rating === 0 && (
                  <p className="text-xs text-gray-400 mt-1">Click a star to rate, click again to clear</p>
                )}
              </div>

              {/* Submit */}
              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {loading ? 'Submitting…' : 'Submit Feedback'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── My Feedback tab ── */}
        {activeTab === 'my-feedback' && (
          <div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2].map(i => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 animate-pulse space-y-3">
                    <div className="flex gap-2">
                      <div className="h-4 bg-gray-200 rounded w-1/3" />
                      <div className="h-5 bg-gray-100 rounded-full w-16" />
                    </div>
                    <div className="h-3 bg-gray-100 rounded w-2/3" />
                    <div className="h-3 bg-gray-100 rounded w-full" />
                  </div>
                ))}
              </div>
            ) : myFeedback.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                  <MessageCircle className="w-7 h-7 text-blue-300" />
                </div>
                <h3 className="text-base font-semibold text-gray-700 mb-1">No feedback yet</h3>
                <p className="text-sm text-gray-400 mb-4">Your submitted feedback will appear here.</p>
                <button
                  onClick={() => setActiveTab('submit')}
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  Submit feedback
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {myFeedback.map(fb => {
                  const typeConf = FEEDBACK_TYPES.find(t => t.value === fb.type);
                  const Icon     = typeConf?.icon || MessageSquare;
                  return (
                    <div key={fb._id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${typeConf?.bg || 'bg-gray-100'}`}>
                            <Icon className={`w-4 h-4 ${typeConf?.color || 'text-gray-500'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h3 className="text-sm font-semibold text-gray-900 truncate">{fb.title}</h3>
                              {fb.rating > 0 && (
                                <span className="flex items-center gap-0.5 text-xs text-gray-400">
                                  <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                                  {fb.rating}/5
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 whitespace-pre-line">{fb.description}</p>
                            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(fb.createdAt)}
                            </p>
                            {fb.adminNotes && (
                              <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
                                <p className="text-xs font-semibold text-blue-700 mb-0.5">Admin response</p>
                                <p className="text-xs text-blue-800">{fb.adminNotes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        {fb.status === 'pending' && (
                          <button
                            onClick={() => deleteFeedback(fb._id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {notification && (
        <NotificationModal
          {...notification}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default UserFeedbackForm;
